import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, X, Copy, Ban, ExternalLink, Send, Clock, Eye, RotateCcw, Layers, Lock } from 'lucide-react';
import AdminNav from '../components/AdminNav';
import { useLanguage } from '../i18n/LanguageContext';
import { useAdminAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL;

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...options });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const fmtDuration = (mins) => {
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${mins / 60}h`;
  if (mins < 10080) return `${mins / 1440}d`;
  return `${mins / 10080}w`;
};

const AccessManagerPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const auth = useAdminAuth();
  const isSuperAdmin = auth.role === 'super_admin';
  const [tokens, setTokens] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [form, setForm] = useState({ scope: 'selected', experience_id: '', profile_id: '', label: '', max_logins: 10, max_concurrent: 2, expires_days: 30, demo_mode: false });

  // Helper to resolve trilingual strings
  const resolveI18n = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value[language] || value.en || Object.values(value).find(v => v) || '';
    return String(value);
  };

  const [showDemoLink, setShowDemoLink] = useState(false);
  const [demoForm, setDemoForm] = useState({ experience_id: '', duration_minutes: 2, label: '' });
  const [demoResult, setDemoResult] = useState(null);
  const [demoCopied, setDemoCopied] = useState(false);

  // Bulk demo links
  const [showBulk, setShowBulk] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkDuration, setBulkDuration] = useState(2);
  const [bulkLabel, setBulkLabel] = useState('Trade Show Demo');
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkCopied, setBulkCopied] = useState(null);
  const [passcodeCopied, setPasscodeCopied] = useState(null);

  // Showroom Invitations
  const [showInvitation, setShowInvitation] = useState(false);
  const [invForm, setInvForm] = useState({ recipient_name: '', company: '', message: '', experience_ids: [], passcode: '', expires_days: 30 });
  const [invResult, setInvResult] = useState(null);
  const [invCopied, setInvCopied] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [invCreating, setInvCreating] = useState(false);

  const copyPasscode = (code, key) => {
    navigator.clipboard.writeText(code).then(() => {
      setPasscodeCopied(key);
      setTimeout(() => setPasscodeCopied(null), 2000);
    });
  };

  const toggleBulkExp = (id) => setBulkSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleInvExp = (id) => setInvForm(f => ({ ...f, experience_ids: f.experience_ids.includes(id) ? f.experience_ids.filter(x => x !== id) : [...f.experience_ids, id] }));

  const fetchInvitations = async () => {
    try {
      const res = await apiFetch('/api/invitations', { headers: { 'Authorization': `Bearer ${auth.token}` } });
      setInvitations(res.items || []);
    } catch { /* silent */ }
  };

  const handleCreateInvitation = async () => {
    if (!invForm.recipient_name.trim() || !invForm.experience_ids.length) return;
    setInvCreating(true); setError('');
    try {
      const result = await apiFetch('/api/invitations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
        body: JSON.stringify(invForm),
      });
      setInvResult(result);
      fetchInvitations();
    } catch (err) { setError(err.message); }
    setInvCreating(false);
  };

  const handleDeleteInvitation = async (id) => {
    if (!window.confirm('Delete this invitation?')) return;
    try {
      await apiFetch(`/api/invitations/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${auth.token}` } });
      fetchInvitations();
    } catch (err) { setError(err.message); }
  };

  const copyInvitation = async (inv) => {
    const invUrl = `${PUBLIC_URL}/invitation/${inv.token}`;
    const firstName = (inv.recipient_name || '').split(' ')[0];
    const logoUrl = `${PUBLIC_URL}/tapelligence_full_logo.png`;
    const spacedCode = (inv.passcode || '').split('').join(' \u2003 ');

    // --- Dynamic body ---
    let bodyHtml = `We&rsquo;ve prepared a personalized Tapelligence showroom experience for you${inv.company ? ` at <strong style="color:#ffffff;">${inv.company}</strong>` : ''}, featuring solutions we&rsquo;ve selected for you.`;
    if (inv.message) bodyHtml += `<br/><br/>${inv.message.replace(/\n/g, '<br/>')}`;
    bodyHtml += `<br/><br/>Explore, interact and see how Tapelligence can help you engage, understand and grow your business.`;

    let bodyPlain = `We've prepared a personalized Tapelligence showroom experience for you${inv.company ? ` at ${inv.company}` : ''}, featuring solutions we've selected for you.`;
    if (inv.message) bodyPlain += `\n\n${inv.message}`;
    bodyPlain += `\n\nExplore, interact and see how Tapelligence can help you engage, understand and grow your business.`;

    // --- Rich HTML email template ---
    const html = [
      '<html><head><meta charset="utf-8"/></head>',
      '<body style="margin:0;padding:0;background-color:#080818;-webkit-text-size-adjust:none;">',
      '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#080818;">',
      '<tr><td align="center" style="padding:32px 16px;">',

      // === Card ===
      '<table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background-color:#0c0c24;border:1px solid #1c1c3c;border-top:2px solid #e87a00;">',

      // -- Top glow accent --
      '<tr><td style="height:3px;background:linear-gradient(90deg,#0c0c24,#e87a00,#0c0c24);font-size:0;line-height:0;">&nbsp;</td></tr>',

      // -- Logo --
      '<tr><td align="center" style="padding:44px 40px 8px;">',
      `<img src="${logoUrl}" alt="Tapelligence" width="260" style="display:block;max-width:260px;height:auto;" />`,
      '</td></tr>',

      // -- Tagline --
      '<tr><td align="center" style="padding:0 40px 36px;font-family:Arial,Helvetica,sans-serif;font-size:11px;letter-spacing:5px;color:#c4953a;">',
      'TAP. &nbsp;ENGAGE. &nbsp;INSIGHT. &nbsp;GROW.',
      '</td></tr>',

      // -- Headline --
      '<tr><td align="center" style="padding:0 32px 4px;font-family:Arial,Helvetica,sans-serif;font-size:12px;letter-spacing:6px;color:#6a6a80;font-weight:400;">',
      'YOUR PERSONALIZED',
      '</td></tr>',
      '<tr><td align="center" style="padding:0 32px 4px;font-family:Georgia,Times,serif;font-size:30px;color:#ffffff;font-weight:bold;line-height:38px;">',
      'SHOWROOM EXPERIENCE',
      '</td></tr>',
      '<tr><td align="center" style="padding:0 32px 28px;font-family:Arial,Helvetica,sans-serif;font-size:12px;letter-spacing:6px;color:#6a6a80;font-weight:400;">',
      'IS READY FOR YOU',
      '</td></tr>',

      // -- Divider --
      '<tr><td align="center" style="padding:0 40px 32px;">',
      '<table cellpadding="0" cellspacing="0" border="0" width="60"><tr><td style="height:2px;background-color:#e87a00;font-size:0;line-height:0;">&nbsp;</td></tr></table>',
      '</td></tr>',

      // -- Greeting --
      `<tr><td style="padding:0 48px 14px;font-family:Arial,Helvetica,sans-serif;font-size:19px;color:#ffffff;font-weight:bold;">`,
      `Hi ${firstName},`,
      '</td></tr>',

      // -- Body --
      `<tr><td style="padding:0 48px 36px;font-family:Arial,Helvetica,sans-serif;font-size:15px;color:#9a9ab0;line-height:26px;">`,
      bodyHtml,
      '</td></tr>',

      // -- CTA Button --
      '<tr><td align="center" style="padding:0 40px 40px;">',
      '<table cellpadding="0" cellspacing="0" border="0"><tr><td align="center" style="border-radius:50px;background-color:#0877fd;background-image:linear-gradient(90deg,#0877fd,#4aa0fd,#fd7f00,#fd7f00);">',
      `<a href="${invUrl}" target="_blank" style="display:inline-block;padding:18px 48px;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;letter-spacing:3px;color:#ffffff;text-decoration:none;border-radius:50px;">`,
      '&#8594; &nbsp;&nbsp;&#9474;&nbsp;&nbsp; PRESS HERE TO BEGIN',
      '</a>',
      '</td></tr></table>',
      '</td></tr>',

      // -- Access Code Box --
      '<tr><td align="center" style="padding:0 40px 40px;">',
      '<table cellpadding="0" cellspacing="0" border="0" style="border:1px solid #2a2a4a;border-radius:14px;">',
      '<tr><td align="center" style="padding:16px 36px 6px;font-family:Arial,Helvetica,sans-serif;font-size:10px;letter-spacing:4px;color:#6a6a80;">',
      '&#128274; &nbsp;YOUR ACCESS CODE',
      '</td></tr>',
      `<tr><td align="center" style="padding:4px 36px 18px;font-family:'Courier New',Courier,monospace;font-size:28px;font-weight:bold;color:#ffffff;letter-spacing:6px;">`,
      spacedCode,
      '</td></tr>',
      '</table>',
      '</td></tr>',

      // -- Features Row --
      '<tr><td style="padding:0 32px 24px;">',
      '<table width="100%" cellpadding="0" cellspacing="0" border="0">',
      '<tr>',
      '<td width="25%" align="center" valign="top" style="padding:8px 4px;font-family:Arial,Helvetica,sans-serif;font-size:10px;letter-spacing:1px;color:#4a6a9a;line-height:16px;"><strong style="font-size:14px;">&#9741;</strong><br/>TAP<br/>EXPERIENCES</td>',
      '<td width="25%" align="center" valign="top" style="padding:8px 4px;font-family:Arial,Helvetica,sans-serif;font-size:10px;letter-spacing:1px;color:#c4953a;line-height:16px;"><strong style="font-size:14px;">&#9783;</strong><br/>REAL<br/>INSIGHTS</td>',
      '<td width="25%" align="center" valign="top" style="padding:8px 4px;font-family:Arial,Helvetica,sans-serif;font-size:10px;letter-spacing:1px;color:#4a6a9a;line-height:16px;"><strong style="font-size:14px;">&#9786;</strong><br/>HAPPIER<br/>GUESTS</td>',
      '<td width="25%" align="center" valign="top" style="padding:8px 4px;font-family:Arial,Helvetica,sans-serif;font-size:10px;letter-spacing:1px;color:#c4953a;line-height:16px;"><strong style="font-size:14px;">&#8599;</strong><br/>STRONGER<br/>BUSINESS</td>',
      '</tr></table>',
      '</td></tr>',

      // -- Divider 2 --
      '<tr><td align="center" style="padding:0 60px 20px;">',
      '<table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="height:1px;background-color:#1c1c3c;font-size:0;line-height:0;">&nbsp;</td></tr></table>',
      '</td></tr>',

      // -- Footer --
      '<tr><td align="center" style="padding:0 40px 6px;font-family:Arial,Helvetica,sans-serif;font-size:10px;letter-spacing:3px;color:#5a5a70;">',
      'THANK YOU FOR EXPLORING TAPELLIGENCE.',
      '</td></tr>',
      '<tr><td align="center" style="padding:0 40px 44px;font-family:Arial,Helvetica,sans-serif;font-size:9px;letter-spacing:2px;color:#44445a;">',
      'WE LOOK FORWARD TO YOUR FEEDBACK.',
      '</td></tr>',

      // === End Card ===
      '</table>',
      '</td></tr></table>',
      '</body></html>',
    ].join('');

    // --- Plain-text fallback ---
    const plainText = [
      'TAPELLIGENCE',
      'Tap. Engage. Insight. Grow.',
      '',
      `Hi ${firstName},`,
      '',
      bodyPlain,
      '',
      'PRESS HERE TO BEGIN \u2192',
      invUrl,
      '',
      `Your access code: ${inv.passcode}`,
      '',
      'Thank you for exploring Tapelligence.',
      'We look forward to your feedback.',
      '',
      'Tapelligence',
    ].join('\n');

    // --- Copy to clipboard: rich HTML + plain-text ---
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        })
      ]);
    } catch {
      try { await navigator.clipboard.writeText(plainText); } catch { /* user denied */ }
    }
    setInvCopied(inv.id);
    setTimeout(() => setInvCopied(false), 2500);
  };

  const shareInvitation = async (inv) => {
    const url = `${PUBLIC_URL}/invitation/${inv.token}`;
    const firstName = (inv.recipient_name || '').split(' ')[0];
    const text = `Tapelligence Showroom\n\nHi ${firstName},\n\nWe've prepared a personalized Tapelligence showroom experience for you.\n\nExplore the experiences we've selected for you:\n${url}\n\nYour access code: ${inv.passcode}`;
    try { await navigator.share({ title: 'Tapelligence Showroom', text }); } catch { /* cancelled */ }
  };

  const handleBulkGenerate = async () => {
    setCreating(true); setError('');
    try {
      const result = await apiFetch('/api/demo-access/bulk-demo-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ experience_ids: bulkSelected, duration_minutes: bulkDuration, label_prefix: bulkLabel }),
      });
      setBulkResults(result.items || []);
      fetchAll();
    } catch (err) { setError(err.message); } finally { setCreating(false); }
  };

  const buildShareText = (name, url, passcode) => {
    const lines = [
      name,
      '',
      'I\'d like you to take a quick look at this Tapelligence experience.',
      '',
      '👉 Open the Demo:',
      url,
    ];
    if (passcode) {
      lines.push('');
      lines.push(`🔐 Access Code: ${passcode}`);
    }
    return lines.join('\n');
  };

  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  const shareDemo = async (name, url, passcode) => {
    const text = buildShareText(name, url, passcode);
    try {
      await navigator.share({ title: name, text });
    } catch { /* user cancelled */ }
  };

  const copyBulkLink = (token, id, passcode, name) => {
    const url = `${PUBLIC_URL}/experience/${token}`;
    const text = buildShareText(name || 'Tapelligence Demo', url, passcode);
    navigator.clipboard.writeText(text).then(() => { setBulkCopied(id); setTimeout(() => setBulkCopied(null), 2000); });
  };

  const copyAllBulkLinks = () => {
    if (!bulkResults) return;
    const header = 'TAPELLIGENCE DEMO EXPERIENCES\n\nHere are a few Tapelligence experiences I\'d like you to explore:\n';
    const blocks = bulkResults.map(r => {
      const url = `${PUBLIC_URL}/experience/${r.token}`;
      const lines = [
        `▸ ${r.experience_name}`,
        '👉 Open Demo:',
        url,
      ];
      if (r.passcode) lines.push(`🔐 Access Code: ${r.passcode}`);
      return lines.join('\n');
    });
    const text = header + '\n' + blocks.join('\n\n');
    navigator.clipboard.writeText(text).then(() => { setBulkCopied('all'); setTimeout(() => setBulkCopied(null), 2000); });
  };

  const handleSendDemoLink = async () => {
    setCreating(true); setError('');
    try {
      const result = await apiFetch('/api/demo-access/send-demo-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(demoForm)
      });
      setDemoResult(result);
      fetchAll();
    } catch (err) { setError(err.message); } finally { setCreating(false); }
  };

  const copyDemoLink = () => {
    if (!demoResult) return;
    const url = `${PUBLIC_URL}/experience/${demoResult.token}`;
    const exp = experiences.find(e => e.id === demoForm.experience_id);
    const name = exp ? displayExpName(exp) : (demoResult.label || 'Tapelligence Demo');
    const text = buildShareText(name, url, demoResult.passcode);
    navigator.clipboard.writeText(text).then(() => { setDemoCopied(true); setTimeout(() => setDemoCopied(false), 2000); });
  };

  const shareDemoLink = () => {
    if (!demoResult) return;
    const url = `${PUBLIC_URL}/experience/${demoResult.token}`;
    const exp = experiences.find(e => e.id === demoForm.experience_id);
    const name = exp ? displayExpName(exp) : (demoResult.label || 'Tapelligence Demo');
    shareDemo(name, url, demoResult.passcode);
  };

  const openDemoPreview = () => {
    if (!demoResult) return;
    window.open(`/experience/${demoResult.token}`, '_blank');
  };

  const handleReactivate = async (tok) => {
    try {
      await apiFetch(`/api/demo-access/${tok.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login_count: 0,
          clear_sessions: true,
          revoked: false,
          session_started_at: null,
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        })
      });
      setCopied(tok.id);
      const url = `${PUBLIC_URL}/experience/${tok.token}`;
      const name = resolveI18n(tok.experience_name) || tok.label || 'Tapelligence Experience';
      const text = (tok.passcode_enabled && tok.passcode) ? buildShareText(name, url, tok.passcode) : url;
      navigator.clipboard.writeText(text).then(() => { setTimeout(() => setCopied(null), 2500); });
      fetchAll();
    } catch (err) { setError(err.message); }
  };

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [tokData, expData, profData] = await Promise.all([
        apiFetch('/api/demo-access'),
        apiFetch('/api/experiences'),
        apiFetch('/api/demo-profiles'),
      ]);
      setTokens(tokData.items || []);
      setExperiences(expData.items || []);
      setProfiles(profData.items || []);
      if (expData.items?.length) setForm(f => ({ ...f, experience_id: f.experience_id || expData.items[0].id }));
      if (profData.items?.length) setForm(f => ({ ...f, profile_id: f.profile_id || profData.items[0].id }));
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); fetchInvitations(); }, [fetchAll]);

  const handleCreate = async () => {
    setCreating(true); setError('');
    try {
      await apiFetch('/api/demo-access', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      setShowCreate(false); setForm(f => ({ ...f, label: '' })); fetchAll();
    } catch (err) { setError(err.message); } finally { setCreating(false); }
  };

  const handleRevoke = async (token) => {
    try {
      await apiFetch(`/api/demo-access/${token.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ revoked: true }) });
      fetchAll();
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (token) => {
    if (!window.confirm('Delete this access token?')) return;
    try { await apiFetch(`/api/demo-access/${token.id}`, { method: 'DELETE' }); fetchAll(); } catch (err) { setError(err.message); }
  };

  const handleRegeneratePasscode = async (tok) => {
    try {
      await apiFetch(`/api/demo-access/${tok.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regenerate_passcode: true })
      });
      fetchAll();
    } catch (err) { setError(err.message); }
  };

  const copyLink = (token) => {
    const url = `${PUBLIC_URL}/experience/${token.token}`;
    const name = resolveI18n(token.experience_name) || token.label || 'Tapelligence Experience';
    if (token.passcode_enabled && token.passcode) {
      const text = buildShareText(name, url, token.passcode);
      navigator.clipboard.writeText(text).then(() => { setCopied(token.id); setTimeout(() => setCopied(null), 2000); });
    } else {
      navigator.clipboard.writeText(url).then(() => { setCopied(token.id); setTimeout(() => setCopied(null), 2000); });
    }
  };

  const isExpired = (t) => t.expires_at && new Date(t.expires_at) < new Date();
  const isExhausted = (t) => t.login_count >= t.max_logins;

  const displayExpName = (exp) => {
    const name = exp?.name;
    if (!name) return 'Unknown';
    if (typeof name === 'string') return name;
    return name.en || Object.values(name).find(v => v) || 'Unknown';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="access-manager-page">
      <AdminNav />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            {error} <button onClick={() => setError('')} className="float-right"><X className="w-4 h-4" /></button>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{t('admin.access.title')}</h1>
            <p className="text-sm text-slate-500 mt-0.5">{t('admin.access.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => { setShowInvitation(true); setInvResult(null); setInvForm({ recipient_name: '', company: '', message: '', experience_ids: [], passcode: '', expires_days: 30 }); }}
              data-testid="create-invitation-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a2e] hover:bg-[#16162a] text-amber-200 text-sm font-semibold rounded-xl border border-amber-800/30">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
              Showroom Invitation
            </button>
            <button onClick={() => { setShowBulk(true); setBulkResults(null); setBulkSelected([]); }}
              data-testid="bulk-demo-links-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl">
              <Layers className="w-4 h-4" /> Bulk Demo Links
            </button>
            <button onClick={() => { setShowDemoLink(true); setDemoResult(null); setDemoCopied(false); if (experiences.length) setDemoForm(f => ({ ...f, experience_id: f.experience_id || experiences[0]?.id })); }}
              data-testid="send-demo-link-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl">
              <Send className="w-4 h-4" /> Send Demo Link
            </button>
            <button onClick={() => setShowCreate(true)} data-testid="generate-access-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] text-white text-sm font-semibold rounded-xl">
              <Plus className="w-4 h-4" /> {t('admin.access.generate')}
            </button>
          </div>
        </div>

        {/* Send Demo Link Dialog */}
        {showDemoLink && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowDemoLink(false)}>
            <div className="bg-white rounded-2xl border border-slate-200 p-6 w-[420px] shadow-2xl" onClick={e => e.stopPropagation()} data-testid="send-demo-link-dialog">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <Send className="w-4.5 h-4.5 text-emerald-600" />
                  </div>
                  <h2 className="text-base font-bold text-slate-900">Send Demo Link</h2>
                </div>
                <button onClick={() => setShowDemoLink(false)} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>

              {demoResult ? (
                <div className="space-y-4" data-testid="demo-link-result">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                    <p className="text-emerald-800 font-semibold text-sm mb-1">Demo Link Generated!</p>
                    <p className="text-emerald-600 text-xs">Timer starts when recipient first opens the link</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Link</p>
                    <p className="text-xs text-slate-700 break-all font-mono">{`${PUBLIC_URL}/experience/${demoResult.token}`}</p>
                  </div>
                  {demoResult.passcode && (
                    <button onClick={() => copyPasscode(demoResult.passcode, 'demo')} className="w-full bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-blue-100 transition-colors" data-testid="demo-passcode-copy-btn">
                      <div>
                        <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mb-0.5 text-left">Passcode {passcodeCopied === 'demo' ? '— Copied!' : '— Click to copy'}</p>
                        <p className="text-xl font-bold text-blue-800 tracking-[0.3em] font-mono" data-testid="demo-passcode-display">{demoResult.passcode}</p>
                      </div>
                      <Lock className="w-5 h-5 text-blue-400" />
                    </button>
                  )}
                  <div className="flex gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {fmtDuration(demoResult.session_duration_minutes)} session</span>
                    <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Passcode protected</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={copyDemoLink} data-testid="demo-link-copy-btn"
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                        demoCopied ? 'bg-emerald-100 text-emerald-700' : 'bg-[#0077FF] hover:bg-[#0066DD] text-white'}`}>
                      <Copy className="w-4 h-4" /> {demoCopied ? '✓ Demo details copied' : 'Copy Demo Details'}
                    </button>
                    {canShare && (
                      <button onClick={shareDemoLink} data-testid="demo-link-share-btn"
                        className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors">
                        <ExternalLink className="w-4 h-4" /> Share
                      </button>
                    )}
                    <button onClick={openDemoPreview} data-testid="demo-link-preview-btn"
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl">
                      <Eye className="w-4 h-4" /> Preview
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Experience</label>
                    <select value={demoForm.experience_id} onChange={e => setDemoForm(f => ({ ...f, experience_id: e.target.value }))}
                      data-testid="demo-experience-select"
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/30">
                      {experiences.map(e => <option key={e.id} value={e.id}>{displayExpName(e)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Duration</label>
                    <select value={demoForm.duration_minutes} onChange={e => setDemoForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) }))}
                      data-testid="demo-duration-select"
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/30">
                      <option value={2}>2 minutes</option>
                      <option value={5}>5 minutes</option>
                      <option value={10}>10 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={240}>4 hours</option>
                      <option value={720}>12 hours</option>
                      <option value={1440}>1 day</option>
                      <option value={4320}>3 days</option>
                      <option value={10080}>1 week</option>
                      <option value={20160}>2 weeks</option>
                      <option value={43200}>30 days</option>
                      <option value={86400}>60 days</option>
                      <option value={129600}>90 days</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Label (optional)</label>
                    <input value={demoForm.label} onChange={e => setDemoForm(f => ({ ...f, label: e.target.value }))}
                      placeholder="e.g. Demo for Client ABC"
                      data-testid="demo-label-input"
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/30" />
                  </div>
                  <button onClick={handleSendDemoLink} disabled={creating || !demoForm.experience_id}
                    data-testid="demo-generate-btn"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl">
                    <Send className="w-4 h-4" /> {creating ? 'Generating...' : 'Generate Demo Link'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Showroom Invitation Dialog */}
        {showInvitation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowInvitation(false)}>
            <div className="bg-white rounded-2xl border border-slate-200 p-6 w-[520px] max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="invitation-dialog">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-[#1a1a2e] rounded-xl flex items-center justify-center">
                    <svg className="w-4 h-4 text-amber-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-900">Create Showroom Invitation</h2>
                    <p className="text-[11px] text-slate-400">Premium personalized invitation</p>
                  </div>
                </div>
                <button onClick={() => setShowInvitation(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4 text-slate-400" /></button>
              </div>

              {!invResult ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Recipient Name *</label>
                    <input value={invForm.recipient_name} onChange={e => setInvForm(f => ({...f, recipient_name: e.target.value}))}
                      placeholder="e.g. Jim McDonald" data-testid="inv-recipient-name"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-200 focus:border-amber-400 outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Company / Organization</label>
                    <input value={invForm.company} onChange={e => setInvForm(f => ({...f, company: e.target.value}))}
                      placeholder="e.g. McDonald's Franchise Group" data-testid="inv-company"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-200 focus:border-amber-400 outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Personal Message</label>
                    <textarea value={invForm.message} onChange={e => setInvForm(f => ({...f, message: e.target.value}))}
                      placeholder="e.g. Jim, we've prepared a few experiences for you to explore."
                      data-testid="inv-message" rows={2}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-200 focus:border-amber-400 outline-none resize-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-2 block">Select Experiences *</label>
                    <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
                      {experiences.map(exp => (
                        <label key={exp.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`inv-exp-toggle-${exp.id}`}>
                          <input type="checkbox" checked={invForm.experience_ids.includes(exp.id)} onChange={() => toggleInvExp(exp.id)}
                            className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-200" />
                          <span className="text-sm text-slate-700">{displayExpName(exp)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Passcode</label>
                      <input value={invForm.passcode} onChange={e => setInvForm(f => ({...f, passcode: e.target.value.replace(/\D/g, '').slice(0,4)}))}
                        placeholder="Auto-generated" maxLength={4} inputMode="numeric" data-testid="inv-passcode"
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-200 focus:border-amber-400 outline-none font-mono tracking-widest" />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Expires (days)</label>
                      <input type="number" value={invForm.expires_days} onChange={e => setInvForm(f => ({...f, expires_days: parseInt(e.target.value) || 30}))}
                        min={1} max={365} data-testid="inv-expires"
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-200 focus:border-amber-400 outline-none" />
                    </div>
                  </div>
                  <button onClick={handleCreateInvitation}
                    disabled={invCreating || !invForm.recipient_name.trim() || !invForm.experience_ids.length}
                    data-testid="inv-create-btn"
                    className="w-full py-3 bg-[#1a1a2e] hover:bg-[#16162a] disabled:bg-slate-200 disabled:text-slate-400 text-amber-200 text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2">
                    {invCreating ? 'Creating...' : 'Create Showroom Invitation'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1e] rounded-2xl p-6 text-center" data-testid="inv-result-card">
                    <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="h-6 mx-auto mb-3 opacity-80" />
                    <p className="text-[9px] font-bold tracking-[0.3em] text-amber-400/60 uppercase mb-4">Showroom Invitation</p>
                    <p className="text-white/90 font-light text-lg mb-1">
                      {invResult.recipient_name}
                    </p>
                    {invResult.company && <p className="text-white/40 text-xs mb-4">{invResult.company}</p>}
                    <p className="text-white/30 text-[11px] mb-4">{invResult.experiences?.length || invResult.experience_ids?.length} experiences selected</p>
                    <div className="inline-flex items-center gap-2 bg-white/[0.05] border border-white/10 rounded-lg px-4 py-2 mb-2">
                      <Lock className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-white font-mono text-lg tracking-[0.3em] font-bold">{invResult.passcode}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => copyInvitation(invResult)} data-testid="inv-copy-btn"
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        invCopied === invResult.id ? 'bg-emerald-100 text-emerald-700' : 'bg-[#1a1a2e] hover:bg-[#16162a] text-amber-200'}`}>
                      <Copy className="w-4 h-4" /> {invCopied === invResult.id ? '✓ Invitation copied' : 'Copy Invitation'}
                    </button>
                    {typeof navigator !== 'undefined' && navigator.share && (
                      <button onClick={() => shareInvitation(invResult)} data-testid="inv-share-btn"
                        className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors">
                        <ExternalLink className="w-4 h-4" /> Share
                      </button>
                    )}
                  </div>
                  <button onClick={() => { setInvResult(null); setInvForm({ recipient_name: '', company: '', message: '', experience_ids: [], passcode: '', expires_days: 30 }); }}
                    className="w-full py-2.5 text-sm text-slate-500 hover:text-slate-700 font-medium">
                    Create another invitation
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invitations List */}
        {invitations.length > 0 && (
          <div className="mb-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="invitations-list">
            <h2 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
              Showroom Invitations
              <span className="text-xs text-slate-400 font-normal">({invitations.length})</span>
            </h2>
            <div className="space-y-2">
              {invitations.map(inv => {
                const isExp = inv.expires_at && new Date(inv.expires_at) < new Date();
                return (
                  <div key={inv.id} className={`flex items-center justify-between p-3 rounded-xl border ${isExp ? 'bg-red-50/50 border-red-100' : 'bg-slate-50 border-slate-100'}`} data-testid={`inv-item-${inv.id}`}>
                    <div className="flex-1 min-w-0 mr-3">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-slate-800 truncate">{inv.recipient_name}</span>
                        {inv.company && <span className="text-xs text-slate-400 truncate">— {inv.company}</span>}
                        {isExp && <span className="text-[10px] font-bold text-red-500 bg-red-100 px-1.5 py-0.5 rounded">EXPIRED</span>}
                        {inv.revoked && <span className="text-[10px] font-bold text-red-500 bg-red-100 px-1.5 py-0.5 rounded">REVOKED</span>}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {inv.experience_names?.length || inv.experience_ids?.length} exp · Passcode: <span className="font-mono font-bold text-slate-600">{inv.passcode}</span>
                        {inv.feedback && <span className="ml-2 text-emerald-600">· Feedback received</span>}
                        <span className={`ml-2 ${inv.last_accessed ? 'text-emerald-600' : 'text-slate-300'}`}>
                          · {inv.last_accessed ? `Viewed ${new Date(inv.last_accessed).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })} ${new Date(inv.last_accessed).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET` : 'Not yet viewed'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => copyInvitation(inv)} title="Copy Invitation" data-testid={`inv-copy-${inv.id}`}
                        className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors">
                        {invCopied === inv.id ? <svg className="w-3.5 h-3.5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                      </button>
                      <button onClick={() => handleDeleteInvitation(inv.id)} title="Delete" data-testid={`inv-delete-${inv.id}`}
                        className="p-1.5 hover:bg-red-100 rounded-lg transition-colors">
                        <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bulk Demo Links Dialog */}
        {showBulk && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowBulk(false)}>
            <div className="bg-white rounded-2xl border border-slate-200 p-6 w-[500px] max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="bulk-demo-dialog">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
                    <Layers className="w-4.5 h-4.5 text-amber-600" />
                  </div>
                  <h2 className="text-base font-bold text-slate-900">Bulk Demo Links</h2>
                </div>
                <button onClick={() => setShowBulk(false)} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>

              {bulkResults ? (
                <div className="space-y-3" data-testid="bulk-results">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                    <p className="text-amber-800 font-semibold text-sm">{bulkResults.length} Demo Links Generated</p>
                    <p className="text-amber-600 text-xs mt-0.5">Timer starts when recipient first opens each link</p>
                  </div>
                  {bulkResults.map(r => (
                    <div key={r.id} className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-semibold text-slate-700">{r.experience_name}</span>
                        <button onClick={() => copyBulkLink(r.token, r.id, r.passcode, r.experience_name)} data-testid={`bulk-copy-${r.id}`}
                          className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${bulkCopied === r.id ? 'bg-emerald-100 text-emerald-700' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                          <Copy className="w-3 h-3" /> {bulkCopied === r.id ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-400 font-mono break-all">{PUBLIC_URL}/experience/{r.token}</p>
                      {r.passcode && (
                        <button onClick={(e) => { e.stopPropagation(); copyPasscode(r.passcode, `bulk-${r.id}`); }} className="mt-1.5 flex items-center gap-1.5 cursor-pointer hover:opacity-70 transition-opacity" data-testid={`bulk-passcode-copy-${r.id}`}>
                          <Lock className="w-3 h-3 text-blue-500" />
                          <span className="text-xs font-bold text-blue-700 tracking-[0.2em] font-mono" data-testid={`bulk-passcode-${r.id}`}>
                            {passcodeCopied === `bulk-${r.id}` ? 'Copied!' : r.passcode}
                          </span>
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={copyAllBulkLinks} data-testid="bulk-copy-all-btn"
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${bulkCopied === 'all' ? 'bg-emerald-100 text-emerald-700' : 'bg-[#0077FF] hover:bg-[#0066DD] text-white'}`}>
                    <Copy className="w-4 h-4" /> {bulkCopied === 'all' ? '✓ All demos copied' : 'Copy All Demos'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Select Experiences</label>
                    <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                      {experiences.map(exp => (
                        <label key={exp.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${bulkSelected.includes(exp.id) ? 'border-amber-300 bg-amber-50/50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}
                          data-testid={`bulk-exp-${exp.id}`}>
                          <input type="checkbox" checked={bulkSelected.includes(exp.id)} onChange={() => toggleBulkExp(exp.id)}
                            className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500/30" />
                          <span className="text-sm font-medium text-slate-700">{displayExpName(exp)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Session Duration</label>
                      <select value={bulkDuration} onChange={e => setBulkDuration(Number(e.target.value))}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-amber-500/30">
                        <option value={2}>2 minutes</option>
                        <option value={5}>5 minutes</option>
                        <option value={10}>10 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={240}>4 hours</option>
                        <option value={720}>12 hours</option>
                        <option value={1440}>1 day</option>
                        <option value={4320}>3 days</option>
                        <option value={10080}>1 week</option>
                        <option value={20160}>2 weeks</option>
                        <option value={43200}>30 days</option>
                        <option value={86400}>60 days</option>
                        <option value={129600}>90 days</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Label Prefix</label>
                      <input value={bulkLabel} onChange={e => setBulkLabel(e.target.value)} placeholder="Trade Show Demo"
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-amber-500/30" />
                    </div>
                  </div>
                  <button onClick={handleBulkGenerate} disabled={creating || bulkSelected.length === 0}
                    data-testid="bulk-generate-btn"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl transition-colors">
                    <Layers className="w-4 h-4" /> {creating ? 'Generating...' : `Generate ${bulkSelected.length} Demo Links`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {showCreate && (
          <div className="mb-6 bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="create-access-form">
            <h2 className="text-sm font-bold text-slate-700">{t('admin.access.newToken')}</h2>

            {/* Scope selector */}
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Access Scope</label>
              <div className="flex gap-2" data-testid="access-scope-selector">
                <button type="button" onClick={() => setForm(f => ({ ...f, scope: 'full_showroom', experience_id: '' }))}
                  data-testid="scope-full-showroom"
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-all ${
                    form.scope === 'full_showroom'
                      ? 'border-[#0077FF] bg-blue-50 text-[#0077FF]'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                  }`}>
                  <Layers className="w-4 h-4" />
                  Full Showroom
                </button>
                <button type="button" onClick={() => setForm(f => ({ ...f, scope: 'selected', experience_id: experiences[0]?.id || '' }))}
                  data-testid="scope-selected"
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-all ${
                    form.scope === 'selected'
                      ? 'border-[#0077FF] bg-blue-50 text-[#0077FF]'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                  }`}>
                  <Eye className="w-4 h-4" />
                  Selected Experience
                </button>
              </div>
              {form.scope === 'full_showroom' && (
                <p className="text-xs text-slate-400 mt-2">Grants access to all current and future Showroom experiences.</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {form.scope === 'selected' && (
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.access.experience')}</label>
                  <select value={form.experience_id} onChange={e => setForm(f => ({ ...f, experience_id: e.target.value }))} data-testid="access-experience-select"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30">
                    {experiences.map(e => <option key={e.id} value={e.id}>{displayExpName(e)}</option>)}
                  </select>
                </div>
              )}
              {form.scope === 'selected' && (
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.access.profile')}</label>
                  <select value={form.profile_id} onChange={e => setForm(f => ({ ...f, profile_id: e.target.value }))} data-testid="access-profile-select"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30">
                    {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.access.label')}</label>
                <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Prospect ABC Corp" data-testid="access-label-input"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.access.expiresDays')}</label>
                <input type="number" value={form.expires_days} onChange={e => setForm(f => ({ ...f, expires_days: parseInt(e.target.value) || 1 }))} data-testid="access-expires-days"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30" />
              </div>
              <div className="pt-1">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Demo Mode</label>
                    <p className="text-[10px] text-slate-400 mt-0.5">When enabled, external destinations receive a demo flag — any passcode is accepted for demonstration purposes.</p>
                  </div>
                  <button type="button" onClick={() => setForm(f => ({ ...f, demo_mode: !f.demo_mode }))}
                    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ml-3 ${form.demo_mode ? 'bg-amber-500' : 'bg-slate-300'}`}
                    data-testid="access-demo-mode-toggle">
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.demo_mode ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-4">
                <Lock className="w-4 h-4 text-blue-500" />
                <p className="text-[10px] text-blue-600 font-medium">Passcode protected — a unique 4-digit code will be generated automatically</p>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button onClick={handleCreate} disabled={creating || (form.scope === 'selected' && !form.experience_id)} data-testid="submit-access-btn"
                className="px-5 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl">
                {creating ? t('admin.common.saving') : t('admin.access.generateToken')}
              </button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2.5 text-sm text-slate-500 hover:text-slate-700">{t('admin.common.cancel')}</button>
            </div>
          </div>
        )}

        {loading ? <div className="text-center py-16 text-slate-400">{t('admin.common.loading')}</div> : tokens.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
            <p className="text-slate-400 text-sm">{t('admin.common.noItems')}</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="access-list">
            {tokens.map(tok => {
              const expired = isExpired(tok);
              const exhausted = isExhausted(tok);
              const isDemoLink = tok.demo_link;
              const demoExpired = isDemoLink && tok.session_started_at && tok.session_duration_minutes &&
                (new Date(tok.session_started_at).getTime() + tok.session_duration_minutes * 60 * 1000) < Date.now();
              const status = tok.revoked ? 'revoked' : (expired || demoExpired) ? 'expired' : exhausted ? 'exhausted' : 'active';
              const statusColors = { active: 'bg-emerald-50 text-emerald-600', revoked: 'bg-red-50 text-red-600', expired: 'bg-slate-100 text-slate-500', exhausted: 'bg-amber-50 text-amber-600' };
              return (
                <div key={tok.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4" data-testid={`access-row-${tok.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-semibold text-slate-800 text-sm truncate">{tok.label || 'Untitled'}</p>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColors[status]}`}>{status}</span>
                        {(tok.scope === 'full_showroom') && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 text-[10px] font-semibold" data-testid={`scope-badge-${tok.id}`}>
                            <Layers className="w-2.5 h-2.5" /> Full Showroom
                          </span>
                        )}
                        {isDemoLink && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-semibold">
                            <Clock className="w-2.5 h-2.5" /> {fmtDuration(tok.session_duration_minutes)} demo
                          </span>
                        )}
                        {tok.demo_mode && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-semibold" data-testid={`demo-mode-badge-${tok.id}`}>
                            <Eye className="w-2.5 h-2.5" /> Demo Mode
                          </span>
                        )}
                        {tok.passcode_enabled && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-semibold" data-testid={`passcode-badge-${tok.id}`}>
                            <Lock className="w-2.5 h-2.5" /> Passcode
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">{resolveI18n(tok.experience_name)} &middot; {resolveI18n(tok.profile_name)}</p>
                      <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500 flex-wrap">
                        {tok.passcode_enabled ? (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); copyPasscode(tok.passcode, `tok-${tok.id}`); }} className="flex items-center gap-1.5 cursor-pointer hover:opacity-70 transition-opacity" data-testid={`passcode-copy-${tok.id}`}>
                              <Lock className="w-3 h-3 text-blue-500" />
                              <span className="font-bold text-blue-700 tracking-[0.15em] font-mono" data-testid={`passcode-value-${tok.id}`}>
                                {passcodeCopied === `tok-${tok.id}` ? 'Copied!' : tok.passcode}
                              </span>
                            </button>
                            <span>{t('admin.access.expires')}: {tok.expires_at ? new Date(tok.expires_at).toLocaleDateString() : '\u2014'}</span>
                            <span className={tok.last_accessed ? 'text-emerald-600' : 'text-slate-300'}>
                              {tok.last_accessed ? `Viewed ${new Date(tok.last_accessed).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })} ${new Date(tok.last_accessed).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET` : 'Not yet viewed'}
                            </span>
                          </>
                        ) : (
                          <>
                            <span>{t('admin.access.logins')}: {tok.login_count}/{tok.max_logins}</span>
                            <span>{t('admin.access.concurrent')}: {tok.max_concurrent}</span>
                            <span>{t('admin.access.expires')}: {tok.expires_at ? new Date(tok.expires_at).toLocaleDateString() : '\u2014'}</span>
                            <span className={tok.last_accessed ? 'text-emerald-600' : 'text-slate-300'}>
                              {tok.last_accessed ? `Viewed ${new Date(tok.last_accessed).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })} ${new Date(tok.last_accessed).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET` : 'Not yet viewed'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => copyLink(tok)} title="Copy Link" data-testid={`copy-link-${tok.id}`}
                        className={`p-1.5 rounded-lg transition-colors ${copied === tok.id ? 'text-emerald-600 bg-emerald-50' : 'text-slate-400 hover:text-[#0077FF] hover:bg-blue-50'}`}>
                        {copied === tok.id ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg> : <Copy className="w-4 h-4" />}
                      </button>
                      <a href={`/experience/${tok.token}`} target="_blank" rel="noopener noreferrer" title="Open" className="p-1.5 text-slate-400 hover:text-[#0077FF] hover:bg-blue-50 rounded-lg transition-colors">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      {!tok.revoked && (
                        <button onClick={() => handleRevoke(tok)} title="Revoke" data-testid={`revoke-btn-${tok.id}`}
                          className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
                          <Ban className="w-4 h-4" />
                        </button>
                      )}
                      {tok.passcode_enabled && !tok.revoked && (
                        <button onClick={() => handleRegeneratePasscode(tok)} title="Regenerate Passcode" data-testid={`regen-passcode-${tok.id}`}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                      {isSuperAdmin && status !== 'active' && (
                        <button onClick={() => handleReactivate(tok)} title="Reactivate" data-testid={`reactivate-btn-${tok.id}`}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                          <RotateCcw className="w-3.5 h-3.5" /> Reactivate
                        </button>
                      )}
                      <button onClick={() => handleDelete(tok)} title="Delete" data-testid={`delete-access-${tok.id}`}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessManagerPage;
